import { type BulkRequestQuery, BulkRequestQueryKey } from "./useBulkRequestQuery";
import {
    type MutationFunctionContext,
    type UseMutationOptions,
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { api } from "api";

type Options = UseMutationOptions<void, Error, boolean>;

/** Turn communication on or off for a particular VyneSync patient id. */
export function useAllowComm(patientId: string, options?: Options) {
    const queryClient = useQueryClient();

    /** Provide built-in onSuccess behavior, then call provided callback if available. */
    const onSuccess = (_: unknown, newValue: boolean, onMutateResult: unknown, context: MutationFunctionContext) => {
        // Invalidate queries that are affected by altering Allow Communication flag
        void queryClient.invalidateQueries({ queryKey: ["patientActivity"] });

        // Rather than invalidating the Bulk Request query (which could cause patients to appear or disappear), just do
        // an optimistic update to reflect the flag has changed (which we trust it has because the API call succeeded).
        queryClient.setQueryData<BulkRequestQuery>(BulkRequestQueryKey, (data) => {
            if (!data || data.busy) {
                return data;
            }

            return {
                ...data,
                rows: data.rows.map((row) =>
                    row.pmsId === patientId ? { ...row, allowCommunication: newValue } : row,
                ),
            };
        });

        // Use callback if provided
        return options?.onSuccess?.(undefined, newValue, onMutateResult, context);
    };

    return useMutation({
        ...options,
        mutationKey: ["setAllowComm", patientId],
        mutationFn: async (newValue: boolean) => await api.provider.bulkAllowComm(patientId, newValue),
        retry: 4,
        retryDelay: (attempt) => 2 ** attempt * 1000,
        onSuccess,
    });
}
